/**
 * 图片预加载和优化工具
 */

class ImagePreloader {
  constructor() {
    this.preloadedImages = new Map()
    this.loadingPromises = new Map()
    this.supportsWebp = null
    
    // 初始化WebP支持检测
    this.detectWebpSupport()
  }

  // 检测WebP支持
  async detectWebpSupport() {
    if (this.supportsWebp !== null) return this.supportsWebp
    
    return new Promise((resolve) => {
      const webp = new Image()
      webp.onload = webp.onerror = () => {
        this.supportsWebp = webp.height === 2
        resolve(this.supportsWebp)
      }
      webp.src = 'data:image/webp;base64,UklGRjoAAABXRUJQVlA4IC4AAACyAgCdASoCAAIALmk0mk0iIiIiIgBoSygABc6WWgAA/veff/0PP8bA//LwYAAA'
    })
  }

  // 获取最优图片格式
  getOptimalImagePath(originalPath) {
    // 如果已经是完整的require路径，直接返回
    if (originalPath.includes('require(')) {
      return originalPath
    }

    const pathWithoutExt = originalPath.replace(/\.[^.]+$/, '')
    
    // 根据WebP支持情况选择格式
    if (this.supportsWebp) {
      try {
        return require(`@/assets/images/${pathWithoutExt}.webp`)
      } catch (e) {
        // WebP不存在，降级到原格式
      }
    }
    
    // 尝试原始格式
    try {
      return require(`@/assets/images/${originalPath}`)
    } catch (e) {
      console.warn(`图片不存在: ${originalPath}`)
      return null
    }
  }

  // 预加载单个图片
  preloadImage(imagePath) {
    if (this.preloadedImages.has(imagePath)) {
      return Promise.resolve(this.preloadedImages.get(imagePath))
    }

    if (this.loadingPromises.has(imagePath)) {
      return this.loadingPromises.get(imagePath)
    }

    const promise = new Promise((resolve, reject) => {
      const img = new Image()
      
      img.onload = () => {
        this.preloadedImages.set(imagePath, img)
        this.loadingPromises.delete(imagePath)
        resolve(img)
      }
      
      img.onerror = () => {
        this.loadingPromises.delete(imagePath)
        reject(new Error(`Failed to load image: ${imagePath}`))
      }
      
      const optimalPath = this.getOptimalImagePath(imagePath)
      if (optimalPath) {
        img.src = optimalPath
      } else {
        reject(new Error(`Image not found: ${imagePath}`))
      }
    })

    this.loadingPromises.set(imagePath, promise)
    return promise
  }

  // 批量预加载图片
  async preloadImages(imagePaths, options = {}) {
    const { 
      concurrent = 6, // 并发加载数量
      priority = [], // 优先加载的图片
      onProgress = null // 进度回调
    } = options

    // 优先加载重要图片
    const priorityImages = imagePaths.filter(path => priority.includes(path))
    const normalImages = imagePaths.filter(path => !priority.includes(path))
    
    let loadedCount = 0
    const totalCount = imagePaths.length

    // 进度更新函数
    const updateProgress = () => {
      loadedCount++
      if (onProgress) {
        onProgress(loadedCount / totalCount, loadedCount, totalCount)
      }
    }

    // 并发加载函数
    const loadBatch = async (paths) => {
      const chunks = []
      for (let i = 0; i < paths.length; i += concurrent) {
        chunks.push(paths.slice(i, i + concurrent))
      }

      for (const chunk of chunks) {
        await Promise.allSettled(
          chunk.map(async (path) => {
            try {
              await this.preloadImage(path)
              updateProgress()
            } catch (error) {
              console.warn(`预加载失败: ${path}`, error)
              updateProgress()
            }
          })
        )
      }
    }

    // 先加载优先图片，再加载普通图片
    if (priorityImages.length > 0) {
      await loadBatch(priorityImages)
    }
    
    if (normalImages.length > 0) {
      await loadBatch(normalImages)
    }

    return {
      success: this.preloadedImages.size,
      total: totalCount,
      failed: totalCount - this.preloadedImages.size
    }
  }

  // 预加载首屏关键图片
  async preloadCriticalImages() {
    const criticalImages = [
      'banner1.png',
      'banner2.png', 
      'banner3.png',
      'logo.png',
      'yellow.png'
    ]

    console.log('开始预加载关键图片...')
    const result = await this.preloadImages(criticalImages, {
      concurrent: 3,
      onProgress: (progress) => {
        console.log(`关键图片加载进度: ${Math.round(progress * 100)}%`)
      }
    })
    
    console.log('关键图片预加载完成:', result)
    return result
  }

  // 根据路由预加载页面图片
  async preloadPageImages(routeName) {
    const pageImageMap = {
      'Home': [
        'banner1.png', 'banner2.png', 'banner3.png', 'banner4.png', 'banner5.png',
        'yellow.png', 'Whoweare-ch.png', 'Whoweare-en.png'
      ],
      'Services': [
        'Services/home-title.png', 'Services/local.png', 'Services/duigou.png',
        'Services/Garden1.png', 'Services/Garden2.png'
      ],
      'About': [
        'Aboutshero.png', 'BrandStory/BrandStoryHeader.png'
      ],
      'Testimonials': [
        'Testimonials/Frame1.png', 'Testimonials/Frame2.png', 'Testimonials/touxiang.png'
      ]
    }

    const images = pageImageMap[routeName] || []
    if (images.length === 0) return

    console.log(`预加载页面图片: ${routeName}`)
    return this.preloadImages(images, {
      concurrent: 4,
      onProgress: (progress) => {
        console.log(`${routeName}页面图片加载: ${Math.round(progress * 100)}%`)
      }
    })
  }

  // 获取预加载的图片
  getPreloadedImage(imagePath) {
    return this.preloadedImages.get(imagePath)
  }

  // 清理预加载缓存
  clearCache() {
    this.preloadedImages.clear()
    this.loadingPromises.clear()
  }

  // 获取缓存统计
  getCacheStats() {
    return {
      preloaded: this.preloadedImages.size,
      loading: this.loadingPromises.size,
      supportsWebp: this.supportsWebp
    }
  }
}

// 创建全局实例
export const imagePreloader = new ImagePreloader()

// Vue插件形式
export const ImagePreloaderPlugin = {
  install(Vue) {
    Vue.prototype.$imagePreloader = imagePreloader
    
    // 全局混入，在路由变化时预加载图片
    Vue.mixin({
      beforeRouteEnter(to, from, next) {
        if (to.name) {
          imagePreloader.preloadPageImages(to.name)
        }
        next()
      }
    })
  }
}

export default imagePreloader
